import { useRef, useState, Dispatch, SetStateAction } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField
} from '@mui/material';
import { updateOperationIncident, OperationIncident } from '@networkRequest/operationsService';
import { useUser } from '@hooks/useUser';
import { PrimaryButton, SecondaryButton } from '@components/index';

interface FormErrors {
  title: string | null;
}

interface FormDataType {
  case_number: string;
  title: string;
}

const defaultFormErrors = {
  title: null,
};

const EditIncidentDetailDialog = ({
  open,
  onClose,
  incident,
  setIncident,
  setError
}: {
  open: boolean;
  onClose: () => void,
  incident: OperationIncident;
  setIncident: Dispatch<SetStateAction<OperationIncident | null>>;
  setError: Dispatch<SetStateAction<string | null>>;
}) => {
  const { user } = useUser();
  const formRef = useRef(null);

  const [formErrors, setFormErrors] = useState<FormErrors>(defaultFormErrors);

  if (!user) return null;

  const validateForm = () => {
    if (!formRef.current) return;

    const formData = new FormData(formRef.current);
    const { title } = Object.fromEntries(formData) as unknown as FormDataType;

    setFormErrors({
      title: !title.length ? 'Title is required' : null,
    });
  };

  const handleCancel = () => {
    setFormErrors(defaultFormErrors);
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    validateForm();

    const hasErrors = Object.values(formErrors).some(Boolean);
    if (hasErrors) return;

    const formData = new FormData(event.currentTarget)
    const { case_number, title } = Object.fromEntries(formData) as unknown as FormDataType;

    const { data, error } = await updateOperationIncident({
      accessToken: user.accessToken,
      incidentId: incident.id,
      payload: {
        case_number,
        title,
      }
    });

    if (data) {
      setIncident(data);
    } else {
      setError(error ?? 'Failed to update incident');
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      slotProps={{
        backdrop: { invisible: true },
        paper: { style: { borderRadius: 8, width: '783px' } }
      }}
    >
      <DialogTitle sx={{ padding: 3 }} variant='h5'>
        Edit Incident Details
      </DialogTitle>
      <form ref={formRef} onSubmit={handleSubmit} noValidate>
        <DialogContent>
          <Grid container spacing={2}>
            <TextField
              fullWidth
              name='case_number'
              label='Case Number'
              defaultValue={incident.case_number ?? ''}
              onBlur={validateForm}
            />
            <TextField
              fullWidth
              required
              name='title'
              label='Title'
              defaultValue={incident.title ?? ''}
              error={!!formErrors.title}
              onBlur={validateForm}
            />
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            paddingTop: 1,
            paddingBottom: 3,
            paddingRight: 3,
            paddingLeft: 3,
          }}
        >
          <SecondaryButton action={handleCancel}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type='submit'>
            Submit
          </PrimaryButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditIncidentDetailDialog;